import { Link, Route } from "react-router-dom";
import { Home as HomeIcon, LayoutGrid, MessageCircleHeart, Wind, Users, Activity, Gem } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { APPS } from "@/lib/apps";
import { useQuantumJourney } from "@/lib/quantum";
import { AppFrame, Page } from "@/components/app/AppKit";
import InstallGuide from "@/components/app/InstallGuide";
import ArielChat from "@/components/app/ArielChat";
import AnkhBreath from "@/components/app/AnkhBreath";
import QuantumGate from "@/components/quantum/QuantumGate";
import GateMap from "@/components/quantum/GateMap";
import { PartnerLobby, PartnerSession } from "@/components/quantum/Partner";
import SacredBox from "@/components/quantum/SacredBox";
import ProgressPanel from "@/components/app/ProgressPanel";
import OurField from "@/components/app/OurField";
import { FieldView, GateList, NextGate, TodayCard } from "@/components/app/GateJourney";

const TABS = [
  ["", "Home", HomeIcon],
  ["gates", "Gates", LayoutGrid],
  ["field", "Field", Activity],
  ["partner", "Partner", Users],
  ["journal", "Journal", Gem],
  ["ariel", "Ariel", MessageCircleHeart],
];

const QuantumHome = () => {
  const { user } = useAuth();
  const { gates } = useQuantumJourney();
  return (
    <Page testId="quantum-home" eyebrow={`Welcome${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`} title={<>Light the <em className="text-[#E8838E]">gates.</em></>}>
      <p className="mt-4 text-[15px] text-[#CFC2B6] leading-relaxed">Each of the 13 Gates has a Quantum layer: the field teachings of Book II that carry it beyond the flesh.</p>
      <TodayCard label="Today’s field practice" />
      <NextGate />
      {gates.length > 0 && <GateMap gates={gates} />}
      <div className="mt-6 grid grid-cols-2 gap-3">
        {[["breath", "Ankhing the Breath", "The circuit beneath it all", Wind], ["partner", "Partner session", "Practise in step, near or far", Users]].map(([to, t, s, Icon]) => (
          <Link key={to} to={`/quantum/${to}`} data-testid={`quantum-tile-${to}`} className="border border-[#D9B47A]/20 p-5 hover:border-[#D9B47A]/60 hover:bg-[#140910] transition-colors">
            <Icon className="w-5 h-5 text-[#D9B47A]" strokeWidth={1.5} />
            <p className="mt-4 font-display text-xl leading-tight">{t}</p>
            <p className="mt-1 text-[13px] text-[#A8998C]">{s}</p>
          </Link>
        ))}
      </div>
      <div className="mt-10"><InstallGuide app={APPS.quantum} /></div>
    </Page>
  );
};

const QuantumGates = () => (
  <Page testId="quantum-gates" eyebrow="The Quantum layers" title={<>The 13 <em className="text-[#E8838E]">Gates</em></>}>
    <GateList />
  </Page>
);

const BADGES = [
  ["first", "First practice", (f, t) => t.total_days >= 1],
  ["streak7", "Seven-day flame", (f, t) => t.streak >= 7],
  ["seal3", "Three seals", (f, t, d) => d >= 3],
  ["partner", "Field coupled", (f) => f.entries.some((e) => e.partner)],
  ["maps10", "Ten field maps", (f) => f.entries.filter((e) => Object.keys(e.after || {}).length).length >= 10],
  ["seal13", "All 13 sealed", (f, t, d) => d >= 13],
];

const QuantumField = () => (
  <Page testId="quantum-field" eyebrow="Your field" title={<>Where the current <em className="text-[#E8838E]">lives.</em></>}>
    <p className="mt-4 text-[15px] text-[#CFC2B6]">Every field map you make after a practice gathers here, so you can see your body opening over time.</p>
    <FieldView badges={BADGES} />
  </Page>
);

const JournalRoute = () => {
  const { gates } = useQuantumJourney();
  return <><SacredBox names={Object.fromEntries(gates.map((x) => [x.g, x.name]))} /><div className="pb-24"><ProgressPanel /></div></>;
};

// "Quantum Intimacy": the second installable app, at /quantum, organised by the 13 Gates.
export default function QuantumApp() {
  return (
    <AppFrame app={APPS.quantum} tabs={TABS}>
      <Route index element={<QuantumHome />} />
      <Route path="gates" element={<QuantumGates />} />
      <Route path="gate/:g" element={<QuantumGate />} />
      <Route path="field" element={<QuantumField />} />
      <Route path="partner" element={<PartnerLobby />} />
      <Route path="partner/:code" element={<PartnerSession />} />
      <Route path="journal" element={<JournalRoute />} />
      <Route path="us" element={<OurField defaultKind="quantum" />} />
      <Route path="ariel" element={<ArielChat />} />
      <Route path="breath" element={<AnkhBreath />} />
      <Route path="*" element={<QuantumHome />} />
    </AppFrame>
  );
}
